import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SearchLocationBadgeComponent } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { PinnedIconComponent } from '@frontendlib/components/components/icon/pinned-icon.component';
import { BaseStitchComponent } from '@modules/common/components/base-stitch/base-stitch.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes, type DragData, isNotAlreadyLinked } from '@modules/drag-n-drop/types/drag-data';
import { FlaggedIconComponent } from '@modules/elements/components/indicator-icons/flagged-icon.component';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { StitchInfoPopoverDirective } from '@modules/elements/directives/stitch-info-popover.directive';
import { TrashActionComponent } from '@modules/elements/components/stitch-trash-action/stitch-trash-action.component';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { StitchItemOpenerService } from '@modules/linked-info/services/stitch-item-opener.service';
import { type ListState as StitchListState } from '@modules/linked-info/types/list-state';
import { UtilsModule } from '@modules/utils/utils.module';
import { toObservable } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import { type StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { type FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { FormContextMenuDirective } from '../menus/form-context-menu.directive';
import { FormsStitchService } from '../services/forms.service';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';

/** Native BaseStitch row: selection, drag payload, actions and linked-item bar retain their shared owners. */
@Component({
  selector: 'app-forms-library-card', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './forms-library-card.component.html',
  styleUrls: ['../../common/components/base-stitch/base-stitch.component.less', '../../elements/components/note/note.component.less', './forms-library-card.component.less'],
  imports: [CommonModule, AsyncPipe, Button2Component, IconComponent, DateFormatPipe, DragNDropModule, FormContextMenuDirective,
    FlaggedIconComponent, PinnedIconComponent, StitchInfoPopoverDirective, LinkedInfoListComponent,
    TrashActionComponent, QuickActionComponent, MatTooltipModule, UtilsModule, SearchLocationBadgeComponent],
})
export class FormsLibraryCardComponent extends BaseStitchComponent<StitchType.FORM, FormSummaryDto> {
  public readonly showLocationBadge = input(false);
  private readonly workspace = inject(FormsWorkspaceStore, { optional: true });
  protected readonly locationBadge = computed<{ name: string } | null>(() => {
    if (!this.showLocationBadge()) {
      return null;
    }
    const folderId = (this.currentItem() as FormSummaryDto | undefined)?.folderUid;
    const folder = folderId ? this.workspace?.folders().find((f) => f.id === folderId) : undefined;
    return folder ? { name: folder.title } : null;
  });
  protected moveDragDataTypes: DragDataTypes[] = [];
  protected dragDataType = DragDataTypes.form;
  protected override containerIdField = 'folderUid';
  protected stitchExpanded = false;
  protected readonly popoverListState: StitchListState = { sort: { by: 'title', order: 'asc' }, filters: { stitch: {} }, view: 'short' };
  private readonly stitchItemOpener = inject(StitchItemOpenerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rowInjector = inject(Injector);
  protected readonly stitchCount$ = inject(LinkedInfoService)
    .trackLinkedInfo(toObservable(this.currentItem), () => this.currentItem() as StitchItemSummaryDto)
    .pipe(map((items) => items.length));
  protected openStitchItem(item: StitchItemSummaryDto): void { this.stitchItemOpener.openFullForm(item, this.rowInjector, this.destroyRef); }
  constructor() { super(inject(Injector), inject(FormsStitchService)); }
  public override dndPredicate = (data: DragData): boolean => {
    if (!this.item || this.item.deleted || !data?.data?.length) return false;
    if ([DragDataTypes.tag, DragDataTypes.knot, DragDataTypes.connection].some(type => type === data.type))
      return isNotAlreadyLinked(data, this.linkedInfo) && data.data.length <= 150 && data.data.every(item => 'name' in item && typeof item.name === 'string' && !!item.name.trim());
    const sources = this.sources(data);
    return sources.data.length > 0 && isNotAlreadyLinked(sources, this.linkedInfo) && validateNativeDragBatch(sources.data, { purpose: 'stitch', spaceId: this.item.accountId, target: this.item }).ok;
  };
  /** BoardDropSources keeps the rest of a multi-selection when it includes its drop target. */
  private sources(data: DragData): DragData {
    return data.type === DragDataTypes.form ? { ...data, data: data.data.filter(item => !(item.id === this.item.id && 'itemType' in item && item.itemType === StitchType.FORM)) } : data;
  }
  public override handleStitch(data: DragData): void { if (this.dndPredicate(data)) super.handleStitch(this.sources(data)); }
  public override handleMove(): void { throw Error('A form row stitches items; filing belongs to its folder target.'); }
}
